// packages/shared-hooks/src/engagements/useEngagementByLead.ts
//
// `GET /engagements/by-lead/:leadId` (#2257 FR-2 / #2247 FR-2) — the card
// shell/summary over the READ-ONLY Engagement projection, resolved by the
// source **Lead UUID** so `/leads/:id` needs no id remap (BR-4). READ-ONLY:
// the card mutates through the legacy `/leads/*` hooks, which invalidate
// `leadsKeys`; this projection refetches on refocus/stale. `enabled` gates the
// fetch on a resolved lead id.
import { useQuery } from '@tanstack/react-query';
import { EngagementDetailViewSchema, type EngagementDetailView } from '@magimp/shared';
import { useApiClient } from '../api/context.js';
import { CRM_V4_FIDELITY_HEADER, engagementsKeys } from './keys.js';

/**
 * @param options.fidelity OPT-IN (#2304): send the `X-Magimp-Crm-V4-Fidelity`
 *   header so the card carries the additive fidelity fields
 *   (`source`/`phone`/`email`/`assigneeName`/…), keyed under a DISTINCT cache
 *   slot so the wider shape never shares an entry with the default read.
 */
export function useEngagementByLead(leadId: string | null, options?: { fidelity?: boolean }) {
  const client = useApiClient();
  const fidelity = options?.fidelity === true;
  return useQuery<EngagementDetailView>({
    queryKey: engagementsKeys.detailByLead(leadId ?? '', { fidelity }),
    enabled: !!leadId,
    queryFn: ({ signal }) =>
      client.get<EngagementDetailView>(
        `/engagements/by-lead/${leadId}`,
        fidelity ? { signal, headers: { [CRM_V4_FIDELITY_HEADER]: '1' } } : { signal },
        EngagementDetailViewSchema,
      ),
  });
}
